import React, { useMemo } from 'react';
import { Box, Button, ButtonGroup, Tooltip, Typography, Skeleton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import { collection, query, where, orderBy } from 'firebase/firestore';
import { db, auth } from '../firebase';
import {
  normalizeExternalUrl,
  openExternalLinkReuseOrFocus,
  openExternalLinkNewTab,
} from '../lib/externalLinkWindows';
import { useFirestoreQuery } from '../hooks/useFirestoreQuery';

const MAX_CHIPS = 12;

/**
 * Horizontal shortcuts to external links the user can open (same rules as External Links tab).
 */
export default function DashboardToolbarLinks({ userData, onOpenAllLinks }) {
  const companyId = userData?.companyId;
  const isCompanyAdmin = userData?.role === 'company_admin';
  const uid = auth.currentUser?.uid;

  const linksQuery = useMemo(() => {
    if (!companyId) return null;
    if (!isCompanyAdmin && !uid) return null;
    const constraints = [where('companyId', '==', companyId)];
    if (!isCompanyAdmin) {
      constraints.push(where('assignedUsers', 'array-contains', uid));
    }
    constraints.push(orderBy('createdAt', 'desc'));
    return query(collection(db, 'links'), ...constraints);
  }, [companyId, isCompanyAdmin, uid]);

  const { data: links, loading } = useFirestoreQuery(linksQuery, {
    enabled: Boolean(companyId && (isCompanyAdmin || uid)),
    silentPermissionDenied: true,
  });

  if (!companyId) return null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', py: 0.25 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontWeight: 600 }}>
          Quick links
        </Typography>
        <Skeleton variant="rounded" height={30} sx={{ flex: 1, maxWidth: 360 }} />
      </Box>
    );
  }

  if (links.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary">
          No shortcuts yet.
        </Typography>
        {onOpenAllLinks && (
          <Button size="small" startIcon={<LinkIcon />} onClick={onOpenAllLinks}>
            External Links
          </Button>
        )}
      </Box>
    );
  }

  const shown = links.slice(0, MAX_CHIPS);
  const overflow = links.length - shown.length;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '100%',
        minHeight: 36,
        py: 0.25,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ flexShrink: 0, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.06 }}
      >
        Quick links
      </Typography>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          overflowX: 'auto',
          flexWrap: 'nowrap',
          py: 0.25,
          '&::-webkit-scrollbar': { height: 4 },
        }}
      >
        {shown.map((link) => {
          const href = normalizeExternalUrl(link.url);
          const label = (link.title || 'Untitled').trim() || 'Link';
          return (
            <ButtonGroup
              key={link.id}
              variant="outlined"
              size="small"
              sx={{ flexShrink: 0 }}
            >
              <Tooltip
                title={href ? 'Open or focus this site (reuses the tab Shifty opened for this link, if it is still open)' : 'No URL'}
                enterDelay={400}
              >
                <span>
                  <Button
                    disabled={!href}
                    onClick={() => openExternalLinkReuseOrFocus(link.id, link.url)}
                    sx={{
                      maxWidth: { xs: 112, sm: 148 },
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textTransform: 'none',
                      fontWeight: 500,
                    }}
                  >
                    {label}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="New tab">
                <span>
                  <Button
                    disabled={!href}
                    aria-label={`New tab: ${label}`}
                    onClick={() => openExternalLinkNewTab(link.url)}
                    sx={{ minWidth: 34, px: 0.5 }}
                  >
                    <AddIcon sx={{ fontSize: 18 }} />
                  </Button>
                </span>
              </Tooltip>
            </ButtonGroup>
          );
        })}
      </Box>
      {onOpenAllLinks && (
        <Box sx={{ display: 'flex', flexShrink: 0, alignItems: 'center', gap: 0.25 }}>
          {overflow > 0 && (
            <Button size="small" variant="text" onClick={onOpenAllLinks}>
              +{overflow} more
            </Button>
          )}
          <Button size="small" variant="text" onClick={onOpenAllLinks}>
            All links
          </Button>
        </Box>
      )}
    </Box>
  );
}
